import { useRef } from "react";
import { createContext, useContext, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import SpecificAPI from "../services/SpecificApi";
import { set } from "date-fns";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const hasLoadedRef = useRef(false);
  const { user, isAuthenticated, isLoading } = useAuth0();

  const [authUser, setAuthUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [userData, setUserData] = useState(
    localStorage.getItem("SA_user_data") ? JSON.parse(localStorage.getItem("SA_user_data")) : {},
  );
  const [userWallet, setUserWallet] = useState(null);
  const [isAppUserLoading, setIsAppUserLoading] = useState(true);

  useEffect(() => {
    if (isLoading) return;

    // 🔁 logout reset
    if (!isAuthenticated || !user) {
      hasLoadedRef.current = false;
      setAuthUser(null);
      setAppUser(null);
      setIsAppUserLoading(false);
      setUserAppData({});
      return;
    }

    // 🛑 load once per login
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadUser = async () => {
      try {
        setIsAppUserLoading(true);
        setAuthUser(user);

        const response = await SpecificAPI.getUsers({
          auth0_id: user.sub,
        });

        let userData = response.data?.[0] || null;

        setAppUser(userData);

        if (userData.role === "teacher" || userData.role === "administrative") {
          const response2 = await SpecificAPI.getAllWallets({
            user_id: userData.id,
          });

          setUserWallet(response2?.[0] || null);
        }
      } catch (err) {
        console.error("User loading failed:", err);
      } finally {
        setIsAppUserLoading(false);
      }
    };

    loadUser();
  }, [isAuthenticated, user, isLoading]);

  const setUserAppData = (data) => {
    setUserData(data);
    localStorage.setItem("SA_user_data", JSON.stringify(data));
  };

  return (
    <UserContext.Provider
      value={{
        authUser,
        appUser,
        isAppUserLoading,
        userData,
        setUserAppData,
        setAppUser,
        userWallet,
        setUserWallet,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserStore = () => useContext(UserContext);
